import {
  Box,
  Card,
  debounce,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Children, useEffect, useMemo, useState } from "react";
import { Link, useQueryWithStore } from "react-admin";
import resourceNames from "../../../common/data/resourceNames";
import { useHistory } from "react-router-dom";
import { from } from "manipula";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import classnames from "classnames";
import { VehicleBreadcrumbSearchField } from "./VehicleBreadcrumbSearchField";
import { KeyboardBackspace, Search } from "@material-ui/icons";
import styles from "./VehicleBreadcrumb.module.css";

const useStyles = makeStyles((theme: Theme) => ({
  breadcrumbViewContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dropdownTypography: {
    padding: theme.spacing(2),
  },
  searchField: {
    width: `calc(100% - ${theme.spacing(2)}px)`,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: "8px",
  },
  anchorDiv: {
    minHeight: "48px",
    minWidth: "48px",
  },
  popupContainer: {
    overflow: "hidden",
  },
  dropdownResults: {
    overflowY: "auto",
    maxHeight: "75vh",
  },
  marginBottom4: {
    marginBottom: "4px",
  },
  marginDense: {
    marginTop: 0,
    marginBottom: 0,
  },
}));

export interface SelectedVehicle {
  id?: number | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  name?: string | null;
}

interface Props {
  vehicle?: SelectedVehicle;
  getBasePath: (vehicle?: SelectedVehicle) => string;
  getLink?: (vehicle?: SelectedVehicle) => string;
  getBackLink?: (vehicle?: SelectedVehicle) => string;
}

interface VehicleNameDisplayProps {
  year?: string | null;
  make?: string | null;
  model?: string | null;
  name?: string | null;
}

interface VehicleNameBreadcrumbViewProps {
  children: React.ReactNode;
  getBasePath: (vehicle?: SelectedVehicle) => string;
  selectedVehicle?: SelectedVehicle;
  getBackLink?: (vehicle?: SelectedVehicle) => string;
}

const VehicleNameDisplay = ({
  year,
  make,
  model,
  name,
}: VehicleNameDisplayProps) => (
  <Box display="flex" flexDirection="row" gridGap={5}>
    <sup>
      <Typography>{name}</Typography>
    </sup>
    <Typography variant="h6">
      {year} {make} {model}
    </Typography>
  </Box>
);

export const VehicleNameBreadcrumbView = ({
  children,
  getBasePath,
  selectedVehicle,
  getBackLink,
}: VehicleNameBreadcrumbViewProps) => {
  const [vehiclesList, setVehiclesList] = useState<SelectedVehicle[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const debounceSetSearchText = useMemo(
    () =>
      debounce((nextValue: string) => {
        setDebouncedSearchText(nextValue);
      }, 500),
    []
  );

  useSearchFields({}, ["name"], resourceNames.ADMIN_VEHICLES_VIEW);
  const { data, loading, total } = useQueryWithStore({
    type: "getList",
    resource: resourceNames.ADMIN_VEHICLES_VIEW,
    payload: {
      sort: {
        field: "name",
        order: "ASC",
      },
      filter: {
        q: debouncedSearchText,
        status: "Active",
      },
      pagination: {
        page: 1,
        perPage: 1000,
      },
    },
  });

  useEffect(() => {
    if (!data || !total) {
      return;
    }

    const list: SelectedVehicle[] = from(
      Object.getOwnPropertyNames(data).map((x) => ({
        id: data[x].id,
        year: data[x].year,
        make: data[x].make,
        model: data[x].model,
        name: data[x].name,
      }))
    )
      .where((x) => x.id)
      .orderBy((x) => x.name)
      .toArray();
    setVehiclesList(list);
  }, [data, total]);

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleVehicleSelected = (vehicle?: SelectedVehicle) => {
    handleClose();
    if (getBasePath && selectedVehicle?.id !== vehicle?.id) {
      history.push(getBasePath(vehicle));
    }
  };

  const classes = useStyles();
  const dropdownOpen = Boolean(anchorEl);
  return (
    <div className={classes.breadcrumbViewContainer}>
      <>
        {Children.only(children)}
        <div className={styles.wrapper}>
          {getBackLink ? (
            <IconButton
              onClick={() => history.push(getBackLink(selectedVehicle))}
            >
              <KeyboardBackspace />
            </IconButton>
          ) : null}
          <TextField
            disabled={!!anchorEl}
            variant="outlined"
            margin="dense"
            className={`appbar-filter appbar-simple-search-field ${
              anchorEl ? "hidden" : ""
            } ${classes.marginDense}`}
            label=""
            value=""
            onClick={handleClick}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {/* <IconButton onClick={handleClick}>
          <Search />
        </IconButton> */}
          <Popover
            className={classes.popupContainer}
            open={dropdownOpen}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <Box>
              <Card>
                <VehicleBreadcrumbSearchField
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    debounceSetSearchText(e.target.value);
                  }}
                  loading={loading}
                  className={classes.searchField}
                />
                <Divider className={classes.marginBottom4} />
              </Card>
              <Card
                className={classnames(
                  classes.dropdownResults,
                  "custom-scrollbar"
                )}
              >
                {vehiclesList.length ? (
                  <List dense>
                    {vehiclesList.map((x) => (
                      <ListItem
                        key={x.id}
                        button
                        onClick={() => handleVehicleSelected(x)}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" flexDirection="row" gridGap={5}>
                              <sup>{x.name}</sup>
                              <Typography variant="body1">
                                {x.year} {x.make} {x.model}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography className={classes.dropdownTypography}>
                    No vehicles found.
                  </Typography>
                )}
              </Card>
            </Box>
          </Popover>
        </div>
      </>
    </div>
  );
};

export const VehicleBreadcrumb = ({
  vehicle,
  getLink,
  getBasePath,
  getBackLink,
}: Props) => {
  const [selectedVehicle, setSelectedVehicle] = useState<SelectedVehicle>();

  useEffect(() => {
    if (vehicle) {
      setSelectedVehicle(vehicle);
    }
  }, [vehicle]);

  const link = getLink ? getLink(selectedVehicle) : undefined;
  const vehicleYear = selectedVehicle?.year?.toString() ?? "";
  const vehicleMake = selectedVehicle?.make ?? "";
  const vehicleModel = selectedVehicle?.model ?? "";
  const vehicleName = selectedVehicle?.name ?? "";
  if (link) {
    return (
      <>
        <VehicleNameBreadcrumbView
          getBasePath={getBasePath}
          selectedVehicle={selectedVehicle}
          getBackLink={getBackLink}
        >
          <Link to={link}>
            <VehicleNameDisplay
              year={vehicleYear}
              make={vehicleMake}
              model={vehicleModel}
              name={vehicleName}
            />
          </Link>
        </VehicleNameBreadcrumbView>
      </>
    );
  }

  return (
    <VehicleNameBreadcrumbView
      getBasePath={getBasePath}
      selectedVehicle={selectedVehicle}
    >
      <VehicleNameDisplay
        year={vehicleYear}
        make={vehicleMake}
        model={vehicleModel}
        name={vehicleName}
      />
    </VehicleNameBreadcrumbView>
  );
};
